/**
 * Per-table search and facet filters.
 *
 * The global filter bar scopes **channel and date range**, which every view
 * shares. This is the other kind of filtering: narrowing the rows of one table
 * by what is in them — an author, a re-run marker, a title. Keeping the two
 * separate matters, because a control that looks global but only affects one
 * table is the same class of error as a chart that ignores the channel filter.
 *
 * The search is a plain substring match across the table's text columns rather
 * than a query language: these tables are hundreds of rows, and the question a
 * reader arrives with is *"where is the Dead Zone episode about the chain
 * mail"*, not a boolean expression.
 */
import type { Column, Table } from '@tanstack/react-table'

export interface Facet {
  /** Column id to filter. */
  id: string
  label: string
  /** Optional display for a raw value — `rerun` reads as "Re-run". */
  format?: (value: string) => string
}

const control = {
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
}

/**
 * Distinct values present in the rows, so a facet never offers an empty result.
 *
 * 🔴 **Read off the core row model, not the faceted one — this was D-1.**
 *
 * `column.getFacetedRowModel()` builds a *separate* row model for that column,
 * re-running the filter pipeline. Measured 10 Sep on Channel Overview with the
 * real shape (13 channels × 30 days), unticking one channel: **39.0s with the
 * faceted row model, 6.2s without** — 85% of the cost of a filter change, for a
 * dropdown of 13 names. In a browser that is long enough to read as a permanent
 * freeze: the click never returns, the tab stops answering, and the renderer
 * sits at 100% of a core.
 *
 * **The trade, stated plainly.** A faceted row model excludes *that column's own*
 * filter, so picking a value does not collapse the dropdown to one option. The
 * core model excludes **every** filter. With a single facet the two are
 * identical, which covers Channel Daily and Spotlights. With two or more facets
 * — Stories — the dropdown can now offer a combination that yields no rows,
 * slightly weakening HR-21. That is visible and recoverable: the toolbar already
 * reports "N of M rows" and offers Clear. A 6x slowdown on every filter change
 * in the app is not a fair price for tightening it.
 */
function options(
  table: Table<unknown>,
  column: Column<never, unknown> | undefined,
): string[] {
  if (!column) return []
  const values = new Set<string>()
  for (const row of table.getCoreRowModel().rows) {
    const value = row.getValue(column.id)
    if (value === null || value === undefined || value === '') continue
    values.add(String(value))
  }
  return [...values].sort()
}

export function TableToolbar<T>({
  table,
  facets = [],
  placeholder = 'Search This Table',
}: {
  // The generic parameter varies per table and none of this touches row data,
  // so the column helpers are read through a widened type rather than
  // threading T through every signature.
  table: Table<T>
  facets?: Facet[]
  placeholder?: string
}) {
  const search = (table.getState().globalFilter as string) ?? ''
  const filtered = table.getFilteredRowModel().rows.length
  const total = table.getPreFilteredRowModel().rows.length
  const narrowed = filtered !== total

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
      <input
        type="search"
        value={search}
        onChange={(event) => table.setGlobalFilter(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="px-2 py-1"
        style={{ ...control, minWidth: '14rem' }}
      />

      {facets.map((facet) => {
        const column = table.getColumn(facet.id) as unknown as Column<never, unknown> | undefined
        const value = (column?.getFilterValue() as string) ?? ''
        const list = options(table as unknown as Table<unknown>, column)
        if (list.length < 2) return null
        return (
          <label key={facet.id} className="flex items-center gap-1">
            <span style={{ color: 'var(--text-label)' }}>{facet.label}</span>
            <select
              value={value}
              onChange={(event) => column?.setFilterValue(event.target.value || undefined)}
              className="px-2 py-1"
              style={control}
            >
              <option value="">All</option>
              {list.map((option) => (
                <option key={option} value={option}>
                  {facet.format ? facet.format(option) : option}
                </option>
              ))}
            </select>
          </label>
        )
      })}

      {narrowed && (
        <>
          <span style={{ color: 'var(--text-secondary)' }}>
            {filtered} of {total} rows
          </span>
          <button
            type="button"
            onClick={() => {
              table.setGlobalFilter('')
              table.resetColumnFilters()
            }}
            className="px-2 py-1"
            style={{ ...control, cursor: 'pointer', color: 'var(--brand-ink)' }}
          >
            Clear
          </button>
        </>
      )}
    </div>
  )
}
